import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronRight,
  Plus,
} from "lucide-react";
import { api } from "../../lib/api";

interface Company {
  id: string;
  slug: string;
  name: string;
  active: boolean;
  created_at: string;
  stats: { active: number; total: number; lastAt: string | null };
}

export function MasterCompanies() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["companies"],
    queryFn: () => api<{ items: Company[] }>("/master/companies"),
  });

  if (isLoading) return <p className="text-slate-500">Lädt…</p>;

  return (
    <div>
      <header className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="page-title">Unternehmen</h1>
          <p className="text-sm text-slate-500">
            {data?.items.length ?? 0} Mandanten
          </p>
        </div>
        <Link to="/master/companies/new" className="btn-primary">
          <Plus className="h-4 w-4" />
          Neues Unternehmen
        </Link>
      </header>

      {!data?.items.length ? (
        <div className="card flex flex-col items-center justify-center py-16 text-center">
          <Building2 className="mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm text-slate-500">Noch keine Unternehmen angelegt.</p>
          <Link to="/master/companies/new" className="btn-primary btn-sm mt-4">
            <Plus className="h-3.5 w-3.5" /> Erstes Unternehmen anlegen
          </Link>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Unternehmen</th>
                <th className="px-4 py-3 font-medium">Aktiv</th>
                <th className="px-4 py-3 font-medium">Anfragen</th>
                <th className="px-4 py-3 font-medium">Letzte Anfrage</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="w-8 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((c) => (
                <tr
                  key={c.id}
                  className="group cursor-pointer transition hover:bg-slate-50"
                  onClick={() => navigate(`/master/companies/${c.slug}`)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-xs font-semibold text-slate-700">
                        {initials(c.name)}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{c.name}</div>
                        <div className="text-xs text-slate-500">/{c.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {c.stats.active}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{c.stats.total}</td>
                  <td className="px-4 py-3 text-slate-500">
                    {c.stats.lastAt
                      ? new Date(c.stats.lastAt).toLocaleDateString("de-DE")
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {c.active ? (
                      <span className="pill bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                        <CheckCircle2 className="h-3 w-3" /> Aktiv
                      </span>
                    ) : (
                      <span className="pill bg-slate-100 text-slate-700 ring-1 ring-slate-200">
                        <AlertTriangle className="h-3 w-3" /> Inaktiv
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-400 group-hover:text-slate-700">
                    <ChevronRight className="h-4 w-4" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
